import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchCustomerBrokers,
  sendProtectionVerificationCode,
  startOptOutProcess,
  verifyProtectionVerificationCode,
} from "./profileProtectionAPI";
import { RootState } from "../../store";
import { logoutAsync } from "../authentication/authenticationSlice";
import { ActionTask } from "./profileProtectionData";

type ProtectionStatus = "not_started" | "in_progress" | "completed";

type ProtectionRequestStatus = "idle" | "loading" | "failed";

type BrokerSiteStatus = {
  brandName: string;
  statusLabel?: string;
};

export interface FetchCustomerBrokersPayload {
  hasRecord?: boolean;
  knownLocations?: unknown[];
  relatives?: unknown[];
  brokerStatuses?: BrokerSiteStatus[];
  inProgress?: boolean;
  completed?: boolean;
  protectedProfile?: {
    email: string | null;
    name: string | null;
    reportToken: string | null;
  };
  brokerSites?: {
    brokerStatuses: BrokerSiteStatus[];
  };
}

export interface ProfileProtectionState {
  status: ProtectionStatus;
  requestStatus: ProtectionRequestStatus;
  activatedFromDashboard: boolean;
  lastBrokerScanDate?: string;
  emailVerified: boolean;
  profileName: string;
  profileLocation: string;
  age?: number;
  knownLocations: string[];
  relatives: string[];
  reportToken?: string;
  defaultBrokerSites: BrokerSiteStatus[];
  brokerSitesStatus: BrokerSiteStatus[];
  actionTasks: ActionTask[];
  alertsEnabled: boolean;
  verificationEmail: string;
  optOutType?: "public" | "site" | "external";
  welcomeDismissed: boolean;
  displayOnReport: boolean;
  reportVersion?: string;
  /**
   * One-shot session gate for the dashboard "Protection Flow Displayed"
   * impression event. Intentionally NOT persisted — resets on page load /
   * fresh redux instance so we get one impression per visit, not one per
   * SPA-navigation back to the dashboard.
   */
  protectionFlowDisplayedSent: boolean;
}

const initialState: ProfileProtectionState = {
  status: "not_started",
  requestStatus: "idle",
  activatedFromDashboard: false,
  lastBrokerScanDate: undefined,
  emailVerified: false,
  profileName: "",
  profileLocation: "",
  age: undefined,
  knownLocations: [],
  relatives: [],
  reportToken: undefined,
  defaultBrokerSites: [],
  brokerSitesStatus: [],
  actionTasks: [],
  alertsEnabled: true,
  verificationEmail: "",
  welcomeDismissed: false,
  displayOnReport: false,
  protectionFlowDisplayedSent: false,
};

// Async thunk for starting opt-out process
export const startOptOutProcessAsync = createAsyncThunk(
  "profileProtection/startOptOutProcess",
  async (_, { getState }) => {
    const state = (getState() as RootState).profileProtection;
    const params = {
      name: state.profileName,
      email: state.verificationEmail,
      reportToken: state.reportToken || "",
      age: state.age,
      knownLocations: state.knownLocations,
      relatives: state.relatives,
    };
    return startOptOutProcess(params);
  },
);
export const fetchCustomerBrokersAsync = createAsyncThunk(
  "profileProtection/fetchCustomerBrokers",
  async (): Promise<FetchCustomerBrokersPayload> =>
    (await fetchCustomerBrokers()) as FetchCustomerBrokersPayload,
);

export const sendProtectionVerificationCodeAsync = createAsyncThunk<
  any,
  { email: string },
  {}
>("profileProtection/sendProtectionVerificationCode", async ({ email }) => {
  return sendProtectionVerificationCode({ email });
});

export const verifyProtectionVerificationCodeAsync = createAsyncThunk<
  any,
  { code: string },
  {}
>("profileProtection/verifyProtectionVerificationCode", async ({ code }) => {
  return verifyProtectionVerificationCode({ code });
});

type ProfileIdentityPayload = {
  name: string;
  location: string;
  reportToken: string;
  reportVersion?: string;
  age?: number;
  knownLocations?: string[];
  relatives?: string[];
};

export const profileProtectionSlice = createSlice({
  name: "profileProtection",
  initialState,
  reducers: {
    setProfileIdentity: (
      state,
      action: PayloadAction<ProfileIdentityPayload>,
    ) => {
      state.profileName = action.payload.name.trim();
      state.profileLocation = action.payload.location.trim();
      state.age = action.payload.age;
      state.knownLocations = action.payload.knownLocations || [];
      state.relatives = action.payload.relatives || [];
      state.reportToken = action.payload.reportToken;
      state.reportVersion = action.payload.reportVersion;
    },
    setVerificationEmail: (state, action: PayloadAction<string>) => {
      state.verificationEmail = action.payload;
      state.emailVerified = false;
    },

    toggleAlerts: state => {
      state.alertsEnabled = !state.alertsEnabled;
    },
    setOptOutType: (
      state,
      action: PayloadAction<"public" | "site" | "external">,
    ) => {
      state.optOutType = action.payload;
    },
    dismissWelcome: state => {
      state.welcomeDismissed = true;
    },
    setDisplayOnReport: (state, action: PayloadAction<boolean>) => {
      state.displayOnReport = action.payload;
    },
    setActivatedFromDashboard: (state, action: PayloadAction<boolean>) => {
      state.activatedFromDashboard = action.payload;
    },
    setProtectionFlowDisplayedSent: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.protectionFlowDisplayedSent = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchCustomerBrokersAsync.pending, state => {
      state.requestStatus = "loading";
    });
    builder.addCase(
      fetchCustomerBrokersAsync.fulfilled,
      (state, action: PayloadAction<FetchCustomerBrokersPayload>) => {
        state.lastBrokerScanDate = new Date().toISOString().slice(0, 10);

        const protectedProfile = action.payload.protectedProfile;
        if (protectedProfile?.email) {
          state.verificationEmail = protectedProfile.email;
        }
        if (protectedProfile?.name) {
          state.profileName = protectedProfile.name;
        }
        if (protectedProfile?.reportToken) {
          state.reportToken = protectedProfile.reportToken;
        }

        const brokerStatuses =
          action.payload.brokerStatuses ||
          action.payload.brokerSites?.brokerStatuses ||
          [];
        state.brokerSitesStatus = brokerStatuses;
        state.defaultBrokerSites = brokerStatuses;

        const hasRecord = action.payload.hasRecord === true;
        const allCompleted =
          hasRecord &&
          brokerStatuses.length > 0 &&
          brokerStatuses.every(site => {
            const statusLabel = site.statusLabel?.toLowerCase();
            return statusLabel === "done";
          });

        state.status = !hasRecord
          ? "not_started"
          : allCompleted
            ? "completed"
            : "in_progress";
        state.requestStatus = "idle";
      },
    );
    builder.addCase(fetchCustomerBrokersAsync.rejected, state => {
      state.requestStatus = "failed";
    });

    builder.addCase(startOptOutProcessAsync.pending, state => {
      state.requestStatus = "loading";
    });
    builder.addCase(startOptOutProcessAsync.fulfilled, state => {
      state.status = "in_progress";
      state.requestStatus = "idle";
    });
    builder.addCase(startOptOutProcessAsync.rejected, state => {
      state.requestStatus = "failed";
    });

    builder.addCase(sendProtectionVerificationCodeAsync.pending, state => {
      state.requestStatus = "loading";
    });
    builder.addCase(sendProtectionVerificationCodeAsync.fulfilled, state => {
      state.requestStatus = "idle";
    });
    builder.addCase(sendProtectionVerificationCodeAsync.rejected, state => {
      state.requestStatus = "failed";
    });

    builder.addCase(verifyProtectionVerificationCodeAsync.pending, state => {
      state.requestStatus = "loading";
    });
    builder.addCase(verifyProtectionVerificationCodeAsync.fulfilled, state => {
      state.emailVerified = true;
      state.requestStatus = "idle";
    });
    builder.addCase(verifyProtectionVerificationCodeAsync.rejected, state => {
      state.requestStatus = "failed";
    });

    builder.addCase(logoutAsync.pending, () => {});
    builder.addCase(logoutAsync.fulfilled, state => {
      state.status = "not_started";
      state.requestStatus = "idle";
      state.activatedFromDashboard = false;
      state.lastBrokerScanDate = undefined;
      state.emailVerified = false;
      state.profileName = "";
      state.profileLocation = "";
      state.age = undefined;
      state.knownLocations = [];
      state.relatives = [];
      state.reportToken = undefined;
      state.reportVersion = undefined;
      state.defaultBrokerSites = [];
      state.brokerSitesStatus = [];
      state.actionTasks = [];
      state.alertsEnabled = true;
      state.verificationEmail = "";
      state.optOutType = undefined;
      state.welcomeDismissed = false;
      state.displayOnReport = false;
      state.protectionFlowDisplayedSent = false;
    });
    builder.addCase(logoutAsync.rejected, () => {});
  },
});

export const {
  setProfileIdentity,
  setVerificationEmail,
  toggleAlerts,
  setOptOutType,
  dismissWelcome,
  setDisplayOnReport,
  setActivatedFromDashboard,
  setProtectionFlowDisplayedSent,
} = profileProtectionSlice.actions;

export const selectProtectionState = (state: RootState) =>
  state.profileProtection;

export const selectProtectionStatus = (state: RootState) =>
  state.profileProtection.status;

export const selectProtectionRequestStatus = (state: RootState) =>
  state.profileProtection.requestStatus;

export const selectBrokerSitesStatus = (state: RootState) =>
  state.profileProtection.brokerSitesStatus;

export const selectProtectionProgress = (state: RootState) => {
  const total = state.profileProtection.brokerSitesStatus.length;
  const removed = state.profileProtection.brokerSitesStatus.filter(
    site => site.statusLabel?.toLowerCase() === "done",
  ).length;
  const percent = total === 0 ? 0 : Math.round((removed / total) * 100);

  return { total, removed, percent };
};

export const selectNeedsAction = (state: RootState) =>
  state.profileProtection.actionTasks;

export const selectDisplayOnReport = (state: RootState) =>
  state.profileProtection.displayOnReport;

export const selectOptOutType = (state: RootState) =>
  state.profileProtection.optOutType;

export const selectActivatedFromDashboard = (state: RootState) =>
  state.profileProtection.activatedFromDashboard;

export const selectProtectionFlowDisplayedSent = (state: RootState) =>
  state.profileProtection.protectionFlowDisplayedSent;

export default profileProtectionSlice.reducer;
